import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { api, apiErr, fileUrl } from "@/lib/api";
import { fmtTemp, fmtDateTime } from "@/lib/format";

export default function IncidenciaDetalle() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inc, setInc] = useState(null);

  const load = () => api.get(`/incidents/${id}`).then(({ data }) => setInc(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const reopen = async () => {
    try { await api.post(`/incidents/${id}/reopen`); toast.success("Incidencia reabierta"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!inc) return <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-3xl">
      <button onClick={() => nav("/admin/incidencias")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="h-4 w-4" /> Incidencias</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">{inc.equipment_name}</h1>
            <p className="text-sm text-slate-500">{inc.location_name}</p>
          </div>
          <div className="flex gap-2"><StatusPill status={inc.status} size="lg" /><StatusPill status={inc.severity} size="lg" /></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <Info label="Rango permitido">{fmtTemp(inc.min_temperature, inc.unit)}–{fmtTemp(inc.max_temperature, inc.unit)}</Info>
          <Info label="Abierta">{fmtDateTime(inc.opened_at)}</Info>
          {inc.resolved_at && <Info label="Resuelta">{fmtDateTime(inc.resolved_at)}</Info>}
        </div>
        {(inc.status === "RESOLVED") && (
          <Button data-testid="reopen-incident-btn" variant="outline" onClick={reopen} className="mt-4 rounded-xl">
            <RotateCcw className="mr-2 h-4 w-4" /> Reabrir incidencia
          </Button>
        )}
      </div>

      <h2 className="mb-3 mt-6 font-display text-lg font-semibold">Acciones correctivas</h2>
      {inc.corrective_actions.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-400">Aún no hay acciones correctivas.</p>
      ) : (
        <div className="space-y-3">
          {inc.corrective_actions.map((a) => (
            <div key={a.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Wrench className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{a.type_name}</p>
                {a.notes && <p className="text-sm text-slate-500">{a.notes}</p>}
                <p className="mt-0.5 text-xs text-slate-400">{a.user_name} · {fmtDateTime(a.created_at)}</p>
              </div>
              {a.photo_url && <img src={fileUrl(a.photo_url)} alt="" className="h-14 w-14 rounded-lg object-cover" />}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-3 mt-6 font-display text-lg font-semibold">Mediciones recientes</h2>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {inc.recent_checks.map((c) => (
          <div key={c.id} className="flex items-center justify-between border-b border-slate-100 p-4 last:border-0">
            <div>
              <span className="tnum font-medium">{fmtTemp(c.confirmed_value, c.unit)}</span>
              <span className="ml-2 text-xs text-slate-400">{fmtDateTime(c.created_at)} · {c.user_name}</span>
            </div>
            <StatusPill status={c.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return <div><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-slate-800">{children}</p></div>;
}
